import { basicInfoAtom, deliveryServiceAtom, takeoutServiceAtom } from "@/atoms";
import { indoorServiceAtom } from "@/atoms/indoor";
import { StaticMap } from "@/components/maps";
import { Clock, FileText, Phone, Pin, Scroll, Storefront, type IconGlyph } from "@/icons";
import { dayOfWeekTranslator } from "@/lib";
import { Service, TimePeriod } from "@/types";
import { useAtomValue } from "jotai";
import { usePageBackground } from "@/hooks/use-page-background";
import { useEffect, useMemo, useState } from "react";

import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AddressSummary,
  Avatar,
  Card,
  Chip,
  ChipGroup,
  DescriptionList,
  Heading,
  KeyValueRow,
  PageContainer,
  SectionHeader,
  Text,
} from "@/components/ui";

type Days = {
  [key: string]: TimePeriod[];
  "Segunda-feira": TimePeriod[];
  "Terça-feira": TimePeriod[];
  "Quarta-feira": TimePeriod[];
  "Quinta-feira": TimePeriod[];
  "Sexta-feira": TimePeriod[];
  "Sábado": TimePeriod[];
  "Domingo": TimePeriod[];
};

const EMPTY_DAYS = (): Days => ({
  "Segunda-feira": [],
  "Terça-feira": [],
  "Quarta-feira": [],
  "Quinta-feira": [],
  "Sexta-feira": [],
  "Sábado": [],
  "Domingo": [],
});

function weekHoursToDays(service: Service | null): Days {
  const days = EMPTY_DAYS();
  if (!service?.serviceHours?.weekHours) return days;

  service.serviceHours.weekHours.forEach((h) => {
    h.dayOfWeek.forEach((day) => {
      const translatedDay = dayOfWeekTranslator(day);
      const timePeriod = h.timePeriods;
      if (!timePeriod) return;
      days[translatedDay].push({
        startTime: format(new Date(`2024-01-01T${timePeriod.startTime}`), "HH:mm", { locale: ptBR }),
        endTime: format(new Date(`2024-01-01T${timePeriod.endTime}`), "HH:mm", { locale: ptBR }),
      });
    });
  });
  return days;
}

const CHANNEL_LABELS: Array<{ key: "DELIVERY" | "TAKEOUT" | "INDOOR"; title: string }> = [
  { key: "DELIVERY", title: "Delivery" },
  { key: "TAKEOUT", title: "Retirada" },
  { key: "INDOOR", title: "Consumo no local" },
];

export function Info() {
  const basicInfo = useAtomValue(basicInfoAtom)
  const deliveryService = useAtomValue(deliveryServiceAtom)
  const takeoutService = useAtomValue(takeoutServiceAtom)
  const indoorService = useAtomValue(indoorServiceAtom)

  const channels = useMemo(() => {
    const byType = {
      DELIVERY: deliveryService,
      TAKEOUT: takeoutService,
      INDOOR: indoorService,
    } as const
    return CHANNEL_LABELS
      .map(({ key, title }) => ({ key, title, service: byType[key] }))
      .filter((c) => c.service != null)
  }, [deliveryService, takeoutService, indoorService])

  const [activeKey, setActiveKey] = useState<"DELIVERY" | "TAKEOUT" | "INDOOR">("DELIVERY")
  const [days, setDays] = useState<Days>(EMPTY_DAYS())
  const today = new Date().toLocaleString("pt-BR", { weekday: "long" });

  useEffect(() => {
    if (channels.length === 0) return
    if (!channels.some((c) => c.key === activeKey)) {
      setActiveKey(channels[0].key)
    }
  }, [channels, activeKey])

  useEffect(() => {
    const active = channels.find((c) => c.key === activeKey)?.service ?? null
    setDays(weekHoursToDays(active))
  }, [channels, activeKey]);

  function handleChannelChange(value: string) {
    const next = channels.find((c) => c.key === value)
    if (next) setActiveKey(next.key)
  }

  usePageBackground()

  if (!basicInfo) throw new Error("Basic info not found")

  return (
    <PageContainer top="header-gap" bottom="chrome">
      <div className="mt-6 flex items-center gap-4">
        {basicInfo?.logoImage?.URL ? (
          <Avatar
            src={basicInfo.logoImage.URL}
            alt="logo"
            crc={basicInfo.logoImage["CRC-32"]}
            priority
            size="xl"
          />
        ) : (
          <Avatar alt="logo" icon={Storefront} size="xl" />
        )}
        <Heading level={1} size="page">
          {basicInfo.name}
        </Heading>
      </div>
      <InfoSection icon={FileText} title="Descrição">
        <Text size="lg">{basicInfo.description}</Text>
      </InfoSection>
      <InfoSection icon={Clock} title="Horário de funcionamento">
        {channels.length > 1 ? (
          <ChipGroup label="Canal de atendimento" value={activeKey} onValueChange={handleChannelChange} layout="wrap" className="mb-3">
            {channels.map((c) => (
              <Chip key={c.key} value={c.key} size="sm" appearance="soft">
                {c.title}
              </Chip>
            ))}
          </ChipGroup>
        ) : channels.length === 1 ? (
          <Text size="sm" weight="semibold" className="mb-2">
            {channels[0].title}
          </Text>
        ) : null}
        {channels.length === 0 ? (
          <Text italic>Horário não informado</Text>
        ) : (
          <DescriptionList divided>
            {Object.entries(days).map(([day, period]) => {
              const isToday = day.toLowerCase() === today;
              return (
                <KeyValueRow
                  key={day}
                  tone={isToday ? "primary" : undefined}
                  strong={isToday}
                  label={`${day}:`}
                  value={
                    period.length === 0 ? (
                      <Text as="span" italic>Fechado</Text>
                    ) : (
                      <span className="flex flex-col items-end">
                        {period.map((p, idx) => (
                          <span key={`${day}-${p.startTime}-${p.endTime}-${idx}`}>
                            {p.startTime} - {p.endTime}
                          </span>
                        ))}
                      </span>
                    )
                  }
                />
              );
            })}
          </DescriptionList>
        )}
      </InfoSection>
      <InfoSection icon={Pin} title="Endereço">
        <AddressSummary address={basicInfo.address} />
        {(basicInfo.address?.postalCode || basicInfo.address?.reference) && (
          <Text size="sm" tone="subtle" className="mt-2">
            {[basicInfo.address.postalCode, basicInfo.address.reference].filter(Boolean).join(" · ")}
          </Text>
        )}
        <div className="relative z-0 mt-4 h-52 w-full overflow-hidden rounded-card">
          <StaticMap latitude={basicInfo?.address?.latitude ?? 0} longitude={basicInfo?.address?.longitude ?? 0} />
        </div>
      </InfoSection>
      <InfoSection icon={Phone} title="Telefones">
        <div className="flex gap-4">
          <Text size="lg">{basicInfo?.contactPhones?.commercialNumber}</Text>
          <Text size="lg">{basicInfo?.contactPhones?.whatsappNumber}</Text>
        </div>
      </InfoSection>
      <InfoSection icon={Scroll} title="CNPJ">
        <Text size="lg">{formatCnpjCpf(basicInfo?.document)}</Text>
      </InfoSection>
    </PageContainer>
  )
}

function InfoSection({ title, icon, children }: {
  title: string
  icon: IconGlyph
  children: React.ReactNode
}) {
  return (
    <Card as="section" padding="lg" className="mt-4">
      <SectionHeader icon={icon} title={title} size="title" />
      <Text as="div" size="lg" tone="muted">
        {children}
      </Text>
    </Card>
  )
}

function formatCnpjCpf(value: string) {
  const cnpjCpf = value.replace(/\D/g, '');

  if (cnpjCpf.length === 11) {
    return cnpjCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "$1.$2.$3-$4");
  }

  return cnpjCpf.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, "$1.$2.$3/$4-$5");
}
